import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  AnimatePresence,
  motion,
  type PanInfo,
  useDragControls,
  useReducedMotion,
} from "framer-motion";
import { type PointerEvent, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useBodyScrollLock } from "@/hooks/use-body-scroll-lock";
import { cn } from "@/lib/cn";
import { preserveComboboxEscape } from "@/lib/combobox-listbox";
import { isProSelectListboxTarget } from "@/lib/pro-select-portal";
import { proThemeClassName, useProThemeStore } from "@/stores/pro-theme";

const SHEET_TRANSITION = { duration: 0.28, ease: [0.32, 0.72, 0, 1] as const };
const OVERLAY_TRANSITION = { duration: 0.2, ease: "easeOut" as const };
const DISMISS_DRAG_OFFSET_PX = 72;
const DISMISS_DRAG_VELOCITY = 380;

type ProBottomSheetMaxHeight = "default" | "full";

interface ProBottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  ariaLabel: string;
  /** `full` grows with content up to the visual viewport (pro mobile shell). */
  maxHeight?: ProBottomSheetMaxHeight;
  /**
   * When false, children own scroll regions (e.g. pinned header + scrollable body).
   * Default true keeps legacy single scroll area for appointment sheets.
   */
  scrollAll?: boolean;
}

type MobileBottomSheetProps = ProBottomSheetProps;

function isInsideBottomSheet(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(".m-bottom-sheet") !== null;
}

function ProBottomSheet({
  open,
  onOpenChange,
  children,
  ariaLabel,
  maxHeight = "default",
  scrollAll = true,
}: ProBottomSheetProps) {
  const theme = useProThemeStore((state) => state.theme);
  const [present, setPresent] = useState(open);
  const invokerRef = useRef<HTMLElement | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const dragControls = useDragControls();
  const prefersReducedMotion = useReducedMotion();
  const sheetTransition = prefersReducedMotion ? { duration: 0 } : SHEET_TRANSITION;
  const overlayTransition = prefersReducedMotion ? { duration: 0 } : OVERLAY_TRANSITION;
  const dragEnabled = !prefersReducedMotion;

  useBodyScrollLock(present);

  useEffect(() => {
    if (open) {
      setPresent(true);
      return;
    }
    setPresent(false);
  }, [open]);

  const requestClose = useCallback(() => {
    setPresent(false);
  }, []);

  const handleExitComplete = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleDragEnd = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      if (info.offset.y > DISMISS_DRAG_OFFSET_PX || info.velocity.y > DISMISS_DRAG_VELOCITY) {
        requestClose();
      }
    },
    [requestClose],
  );

  const handleDragHandlePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!dragEnabled) {
        return;
      }
      dragControls.start(event);
    },
    [dragControls, dragEnabled],
  );

  useEffect(() => {
    if (!present) return;

    const blockBackgroundTouchMove = (event: TouchEvent) => {
      if (isInsideBottomSheet(event.target) || isProSelectListboxTarget(event.target)) {
        return;
      }
      event.preventDefault();
    };

    document.addEventListener("touchmove", blockBackgroundTouchMove, { passive: false });
    return () => document.removeEventListener("touchmove", blockBackgroundTouchMove);
  }, [present]);

  return (
    <DialogPrimitive.Root
      open={present}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) requestClose();
      }}
    >
      <DialogPrimitive.Portal forceMount>
        <AnimatePresence onExitComplete={handleExitComplete}>
          {present ? (
            <motion.div
              key="mobile-bottom-sheet"
              className="m-bottom-sheet-overlay pointer-events-auto fixed inset-0 z-50 flex touch-none flex-col justify-end overscroll-none bg-black/40"
              role="presentation"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={overlayTransition}
              onClick={(event) => {
                if (event.target === event.currentTarget) requestClose();
              }}
            >
              <DialogPrimitive.Content
                forceMount
                asChild
                aria-label={ariaLabel}
                aria-modal="true"
                aria-describedby={undefined}
                onEscapeKeyDown={preserveComboboxEscape}
                onOpenAutoFocus={() => {
                  invokerRef.current =
                    document.activeElement instanceof HTMLElement ? document.activeElement : null;
                }}
                onCloseAutoFocus={(event) => {
                  event.preventDefault();
                  const focusedDialog = document.activeElement?.closest('[role="dialog"]');
                  if (focusedDialog && focusedDialog !== sheetRef.current) return;
                  if (invokerRef.current?.isConnected)
                    invokerRef.current.focus({ preventScroll: true });
                }}
                onPointerDownOutside={(event) => {
                  // The existing overlay dismisses after a click, so a scroll that
                  // starts outside the sheet cannot dismiss it on pointer down.
                  event.preventDefault();
                }}
              >
                <motion.div
                  ref={sheetRef}
                  className={cn(
                    "m-bottom-sheet relative flex h-auto w-full touch-auto flex-col overflow-hidden rounded-t-[20px] bg-pro-card pt-2 text-pro-text shadow-[0_-8px_32px_rgba(0,0,0,0.35)]",
                    maxHeight === "full"
                      ? "max-h-[calc(var(--visual-vh,100vh)-var(--visual-offset-top,0px))]"
                      : "max-h-[calc(var(--visual-vh,100vh)*0.6)]",
                    proThemeClassName(theme),
                  )}
                  data-pro-surface="mobile"
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={sheetTransition}
                  drag={dragEnabled ? "y" : false}
                  dragControls={dragControls}
                  dragListener={false}
                  dragConstraints={{ top: 0, bottom: 0 }}
                  dragElastic={{ top: 0, bottom: 0.45 }}
                  onDragEnd={dragEnabled ? handleDragEnd : undefined}
                  onClick={(event) => event.stopPropagation()}
                >
                  <DialogPrimitive.Title className="sr-only">{ariaLabel}</DialogPrimitive.Title>
                  <div
                    className="m-bottom-sheet-drag-zone flex shrink-0 cursor-grab touch-none flex-col items-center px-6 pt-1 pb-2 active:cursor-grabbing"
                    onPointerDown={handleDragHandlePointerDown}
                  >
                    <div
                      className="m-bottom-sheet-handle h-1 w-9 shrink-0 rounded-full bg-pro-border"
                      aria-hidden
                    />
                  </div>
                  <div
                    className={cn(
                      "flex min-h-0 flex-1 flex-col",
                      scrollAll
                        ? "overflow-y-auto overscroll-y-contain touch-pan-y"
                        : "overflow-hidden",
                    )}
                    {...(scrollAll ? { "data-bottom-sheet-scroll": "" } : {})}
                  >
                    {children}
                  </div>
                </motion.div>
              </DialogPrimitive.Content>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

const MobileBottomSheet = ProBottomSheet;

export type { MobileBottomSheetProps, ProBottomSheetMaxHeight, ProBottomSheetProps };
export { MobileBottomSheet, ProBottomSheet };
